import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  Settings,
  Plus,
  Edit,
  Archive,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Folder,
  Tag,
  Save,
  X,
  ArchiveRestore
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDropdowns = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [sectors, setSectors] = useState([]);
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  
  // Dialog states
  const [sectorDialogOpen, setSectorDialogOpen] = useState(false);
  const [nicheDialogOpen, setNicheDialogOpen] = useState(false);
  const [editingSector, setEditingSector] = useState(null);
  const [editingNiche, setEditingNiche] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [sectorForm, setSectorForm] = useState({ name: '', display_order: 0, archived: false });
  const [nicheForm, setNicheForm] = useState({ name: '', sector_id: '', display_order: 0, archived: false });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      toast.error('Admin access required');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sectorsRes, nichesRes] = await Promise.all([
        axios.get(`${API}/settings/sectors`),
        axios.get(`${API}/settings/niches`)
      ]);
      setSectors(sectorsRes.data);
      setNiches(nichesRes.data);
    } catch (error) {
      toast.error('Failed to load dropdown settings');
    } finally {
      setLoading(false);
    }
  };

  // Sector handlers
  const openAddSector = () => {
    setEditingSector(null);
    setSectorForm({ name: '', display_order: sectors.length, archived: false });
    setSectorDialogOpen(true);
  };

  const openEditSector = (sector) => {
    setEditingSector(sector);
    setSectorForm({ 
      name: sector.name, 
      display_order: sector.display_order, 
      archived: sector.archived || false 
    });
    setSectorDialogOpen(true);
  };

  const handleSaveSector = async () => {
    if (!sectorForm.name.trim()) {
      toast.error('Sector name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingSector) {
        await axios.put(`${API}/settings/sectors/${editingSector.id}`, sectorForm);
        toast.success('Sector updated');
      } else {
        await axios.post(`${API}/settings/sectors`, sectorForm);
        toast.success('Sector created');
      }
      setSectorDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sector');
    } finally {
      setSaving(false);
    }
  };

  const toggleSectorArchive = async (sector) => {
    try {
      await axios.put(`${API}/settings/sectors/${sector.id}`, {
        ...sector,
        archived: !sector.archived
      });
      toast.success(sector.archived ? 'Sector restored' : 'Sector archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to update sector');
    }
  };

  // Niche handlers
  const openAddNiche = (sectorId = '') => {
    setEditingNiche(null);
    setNicheForm({ name: '', sector_id: sectorId, display_order: niches.length, archived: false });
    setNicheDialogOpen(true);
  };

  const openEditNiche = (niche) => {
    setEditingNiche(niche);
    setNicheForm({ 
      name: niche.name, 
      sector_id: niche.sector_id,
      display_order: niche.display_order, 
      archived: niche.archived || false 
    });
    setNicheDialogOpen(true);
  };

  const handleSaveNiche = async () => {
    if (!nicheForm.name.trim() || !nicheForm.sector_id) {
      toast.error('Niche name and sector are required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingNiche) {
        await axios.put(`${API}/settings/niches/${editingNiche.id}`, nicheForm);
        toast.success('Niche updated');
      } else {
        await axios.post(`${API}/settings/niches`, nicheForm);
        toast.success('Niche created');
      }
      setNicheDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save niche');
    } finally {
      setSaving(false);
    }
  };

  const toggleNicheArchive = async (niche) => {
    try {
      await axios.put(`${API}/settings/niches/${niche.id}`, {
        ...niche,
        archived: !niche.archived
      });
      toast.success(niche.archived ? 'Niche restored' : 'Niche archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to update niche');
    }
  };

  // Filter and group data
  const activeSectors = sectors.filter(s => !s.archived);
  const archivedSectors = sectors.filter(s => s.archived);
  const sectorMap = sectors.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

  const getNichesForSector = (sectorId) => {
    return niches.filter(n => n.sector_id === sectorId && (showArchived || !n.archived));
  };

  if (!isAdmin) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="admin-dropdowns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Settings className="w-8 h-8" />
            Dropdown Manager
          </h1>
          <p className="text-muted-foreground mt-1">Manage sectors, niches, and other system dropdowns</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch 
              checked={showArchived} 
              onCheckedChange={setShowArchived} 
              id="show-archived"
            />
            <Label htmlFor="show-archived" className="text-sm text-muted-foreground cursor-pointer">
              Show archived
            </Label>
          </div>
          <Button variant="outline" onClick={fetchData} className="border-border" data-testid="refresh-dropdowns">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Tabs defaultValue="sectors" className="space-y-6">
        <TabsList className="bg-secondary">
          <TabsTrigger value="sectors">Sectors & Niches</TabsTrigger>
        </TabsList>

        <TabsContent value="sectors" className="space-y-4">
          {/* Add Sector Button */}
          <div className="flex justify-end">
            <Button onClick={openAddSector} data-testid="add-sector-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Sector
            </Button>
          </div>

          {/* Sectors List */}
          <div className="space-y-4">
            {(showArchived ? sectors : activeSectors).map(sector => (
              <Card 
                key={sector.id} 
                className={`bg-card border-border ${sector.archived ? 'opacity-60' : ''}`}
                data-testid={`sector-${sector.id}`}
              >
                <Collapsible defaultOpen={!sector.archived}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-secondary/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Folder className="w-5 h-5 text-primary" />
                          {sector.name}
                          {sector.archived && (
                            <Badge variant="outline" className="text-xs ml-2">Archived</Badge>
                          )}
                          <Badge variant="secondary" className="ml-2">
                            {getNichesForSector(sector.id).length} niches
                          </Badge>
                        </CardTitle>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditSector(sector)}
                            title="Edit Sector"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleSectorArchive(sector)}
                            title={sector.archived ? 'Restore' : 'Archive'}
                            className={sector.archived ? 'text-emerald-400 hover:text-emerald-300' : 'text-amber-400 hover:text-amber-300'}
                          >
                            {sector.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                          </Button>
                          <ChevronDown className="w-5 h-5" />
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        {getNichesForSector(sector.id).map(niche => (
                          <div 
                            key={niche.id}
                            className={`flex items-center justify-between p-3 bg-secondary/30 rounded-lg border border-border ${niche.archived ? 'opacity-60' : ''}`}
                            data-testid={`niche-${niche.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <Tag className="w-4 h-4 text-muted-foreground" />
                              <span>{niche.name}</span>
                              {niche.archived && (
                                <Badge variant="outline" className="text-xs">Archived</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditNiche(niche)}
                                title="Edit Niche"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleNicheArchive(niche)}
                                title={niche.archived ? 'Restore' : 'Archive'}
                                className={niche.archived ? 'text-emerald-400 hover:text-emerald-300' : 'text-amber-400 hover:text-amber-300'}
                              >
                                {niche.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>
                        ))}
                        {getNichesForSector(sector.id).length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">No niches in this sector</p>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openAddNiche(sector.id)}
                          className="w-full mt-2 border-dashed border-border"
                          data-testid={`add-niche-${sector.id}`}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Niche to {sector.name}
                        </Button>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            ))}
          </div>

          {activeSectors.length === 0 && !showArchived && (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">No sectors configured yet. Click "Add Sector" to get started.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Sector Dialog */}
      <Dialog open={sectorDialogOpen} onOpenChange={setSectorDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editingSector ? 'Edit Sector' : 'Add Sector'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Sector Name *</Label>
              <Input
                value={sectorForm.name}
                onChange={(e) => setSectorForm({ ...sectorForm, name: e.target.value })}
                className="bg-secondary border-border"
                placeholder="e.g., Healthcare, Legal, etc."
                data-testid="sector-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Display Order</Label>
              <Input
                type="number"
                value={sectorForm.display_order}
                onChange={(e) => setSectorForm({ ...sectorForm, display_order: parseInt(e.target.value) || 0 })}
                className="bg-secondary border-border"
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">Lower numbers appear first</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSectorDialogOpen(false)} className="border-border">
              Cancel
            </Button>
            <Button onClick={handleSaveSector} disabled={saving} data-testid="save-sector-btn">
              {saving ? 'Saving...' : (editingSector ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Niche Dialog */}
      <Dialog open={nicheDialogOpen} onOpenChange={setNicheDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editingNiche ? 'Edit Niche' : 'Add Niche'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Sector *</Label>
              <Select 
                value={nicheForm.sector_id} 
                onValueChange={(v) => setNicheForm({ ...nicheForm, sector_id: v })}
              >
                <SelectTrigger className="bg-secondary border-border" data-testid="niche-sector-select">
                  <SelectValue placeholder="Select sector" />
                </SelectTrigger>
                <SelectContent>
                  {activeSectors.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Niche Name *</Label>
              <Input
                value={nicheForm.name}
                onChange={(e) => setNicheForm({ ...nicheForm, name: e.target.value })}
                className="bg-secondary border-border"
                placeholder="e.g., Dentist, HVAC, etc."
                data-testid="niche-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Display Order</Label>
              <Input
                type="number"
                value={nicheForm.display_order}
                onChange={(e) => setNicheForm({ ...nicheForm, display_order: parseInt(e.target.value) || 0 })}
                className="bg-secondary border-border"
                placeholder="0"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNicheDialogOpen(false)} className="border-border">
              Cancel
            </Button>
            <Button onClick={handleSaveNiche} disabled={saving} data-testid="save-niche-btn">
              {saving ? 'Saving...' : (editingNiche ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDropdowns;
